import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { User } from '../../../Types';

type FormData = Partial<Exclude<User, 'id'>>;

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  formData: FormData;
  onFormChange: (field: string, value: string) => void;
  onSubmit: () => void;
}

export default function AddUserDialog({
  open,
  onClose,
  formData,
  onFormChange,
  onSubmit,
}: AddUserDialogProps) {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New User</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Required Fields */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Required Information
            </Typography>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Email"
              value={formData.userEmail}
              onChange={(e) => onFormChange('userEmail', e.target.value)}
              required
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>User Type</InputLabel>
              <Select
                value={formData.userType}
                label="User Type *"
                onChange={(e) => onFormChange('userType', e.target.value)}
              >
                <MenuItem value="Government">Government</MenuItem>
                <MenuItem value="Tyto">Tyto</MenuItem>
                <MenuItem value="External">External</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.contact?.name}
              onChange={(e) => onFormChange('contact.name', e.target.value)}
              required
            />
          </Grid>

          {/* Contact Information */}
          <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Contact Information
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={formData.contact?.address}
              onChange={(e) => onFormChange('contact.address', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Government Email"
              value={formData.contact?.govEmail}
              onChange={(e) => onFormChange('contact.govEmail', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Work Email"
              value={formData.contact?.workEmail}
              onChange={(e) =>
                onFormChange('contact.workEmail', e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Personal Phone"
              value={formData.contact?.personalPhone}
              onChange={(e) =>
                onFormChange('contact.personalPhone', e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Work Phone"
              value={formData.contact?.workPhone}
              onChange={(e) =>
                onFormChange('contact.workPhone', e.target.value)
              }
            />
          </Grid>

          {/* Roles Information */}
          <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Default Role Assignment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formData.roles && formData.roles.length
                ? `Default role: ${formData.roles[0].roleName}`
                : 'No default role found'}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor:
              theme.palette.mode === 'light' ? 'transparent' : 'inherit',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={
            !formData.userEmail || !formData.contact?.name || !formData.userType
          }
          sx={{
            borderRadius: 4,
            px: 2,
            backgroundColor:
              theme.palette.mode === 'light'
                ? 'transparent'
                : (theme) => theme.palette.primary.main,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : (theme) => theme.palette.primary.dark,
            },
            color:
              theme.palette.mode === 'light'
                ? 'inherit'
                : (theme) => theme.palette.primary.contrastText,
            border:
              theme.palette.mode === 'light' ? '1px solid #B8B8B8' : 'none',
          }}
        >
          Create User
        </Button>
      </DialogActions>
    </Dialog>
  );
}
